import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { WindowRefService } from 'i2g-ng-utils';
import { filter, startWith } from 'rxjs/operators';

export interface ILocalStorageKeyValue {
  key: string;
  value: string;
}

@Injectable()
export class LocalStorageService {
  private _localStorage: any;
  private _isLocalStorageSupported: boolean;
  private _localStorageChanges: Subject<ILocalStorageKeyValue> = new Subject<ILocalStorageKeyValue>();

  localStorageChanges$: Observable<ILocalStorageKeyValue> = this._localStorageChanges
    .asObservable()
    .pipe(filter((lsc) => !!lsc));

  constructor(windowRefService: WindowRefService) {
    try {
      this._localStorage = windowRefService.nativeWindow.localStorage;

      if (typeof this._localStorage === 'object') {
        this._localStorage.setItem('localStorage', 1);
        this._localStorage.getItem('localStorage');
        this._localStorage.removeItem('localStorage');

        this._isLocalStorageSupported = true;
      } else {
        throw new Error('local storage not supported');
      }
    } catch (e) {
      Storage.prototype._setItem = Storage.prototype.setItem;
      Storage.prototype.setItem = function () {};
      Storage.prototype._getItem = Storage.prototype.getItem;
      Storage.prototype.getItem = function (key: string) {
        return null;
      };

      this._isLocalStorageSupported = false;
    }
  }

  getItem(key: string): string {
    if (this._isLocalStorageSupported) {
      return this._localStorage.getItem(key);
    } else {
      return null;
    }
  }

  setItem(key: string, value: string | number | boolean) {
    if (this._isLocalStorageSupported) {
      this._localStorage.setItem(key, value);

      this._localStorageChanges.next({ key, value: '' + value });
    } else {
      this._localStorageChanges.next(null);
    }
  }

  removeItem(key: string) {
    if (this._isLocalStorageSupported) {
      this._localStorage.removeItem(key);

      this._localStorageChanges.next({ key, value: null });
    } else {
      this._localStorageChanges.next(null);
    }
  }

  onKeyChange(
    storageKey: string,
    startWithInitialValue?: boolean
  ): Observable<ILocalStorageKeyValue> {
    const obs = this.localStorageChanges$.pipe(
      filter(({ key }: ILocalStorageKeyValue) => storageKey === key)
    );

    return startWithInitialValue
      ? obs.pipe(
          startWith({
            key: storageKey,
            value: this.getItem(storageKey),
          })
        )
      : obs;
  }
}
